import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, Button, Input, cx } from '../../components/ui';
import { ApiError } from '../../lib/api';
import {
  adminApi,
  formatCount,
  formatDate,
  type AdminUserRow,
  type GrantOutcome,
  type PromotionSettings,
  type PromotionState,
} from '../../lib/adminApi';
import { usePageTitle } from '../../lib/usePageTitle';
import { AdminPanelState, StatTile } from './AdminLayout';

const PAGE_SIZE = 25;

/**
 * The filters an operator actually works from.
 *
 * `awaiting_payment` leads because it is the question this screen exists to
 * answer: who is on a paid plan whose promotional window has closed with
 * nothing paying for it. Those are the accounts the payment gateway is late
 * for, and the ones a grant is meant for.
 */
const FILTERS: { value: PromotionState | ''; label: string }[] = [
  { value: 'awaiting_payment', label: 'Awaiting payment' },
  { value: 'ending_soon', label: 'Ending soon' },
  { value: 'active', label: 'Promotion running' },
  { value: 'lapsed', label: 'Promotion ended' },
  { value: 'none', label: 'Never enrolled' },
  { value: '', label: 'All accounts' },
];

/**
 * The launch promotion.
 *
 * Two controls that look similar and are not, so the page keeps them visually
 * apart and says which is which:
 *
 * * **The programme** decides whether *new* accounts are enrolled. Switching it
 *   off shortens nobody's existing window.
 * * **A grant** gives named accounts another window, and works whether the
 *   programme is on or off — the case for it is strongest once enrolment has
 *   stopped and somebody's window has run out before we could take their money.
 */
export function AdminPromotions() {
  usePageTitle('Promotions — WaveQA Admin');

  const overview = useQuery({
    queryKey: ['admin', 'promotions'],
    queryFn: adminApi.getPromotions,
  });

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl font-semibold text-white">Launch promotion</h1>
        <p className="mt-1 max-w-2xl text-sm text-hull-400">
          Accounts on the promotion can run any plan without paying until their
          window closes. Nothing is charged while it is open, and no payment is
          recorded against the account.
        </p>
      </div>

      <AdminPanelState loading={overview.isPending} error={overview.error}>
        {overview.data && (
          <div className="space-y-8">
            {!overview.data.settings.payment_gateway_available && (
              <Alert tone="warn">
                <strong className="text-white">No payment gateway is configured.</strong>{' '}
                Accounts whose window closes on a paid plan cannot pay, and will
                stop running until they move to the free plan or are granted
                another window. Keep an eye on{' '}
                <em className="text-warn-400">Awaiting payment</em> below.
              </Alert>
            )}

            <section className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <StatTile
                label="Running"
                value={formatCount(overview.data.stats.active)}
                hint="window open"
              />
              <StatTile
                label="Ending soon"
                value={formatCount(overview.data.stats.ending_soon)}
                hint={`within ${overview.data.stats.ending_soon_days} days`}
              />
              <StatTile
                label="Awaiting payment"
                value={formatCount(overview.data.stats.awaiting_payment)}
                hint="paid plan, nothing paying for it"
              />
              <StatTile
                label="Never enrolled"
                value={formatCount(overview.data.stats.never_enrolled)}
              />
            </section>

            <ProgrammeCard settings={overview.data.settings} />
          </div>
        )}
      </AdminPanelState>

      <GrantPanel />
    </div>
  );
}

function ProgrammeCard({ settings }: { settings: PromotionSettings }) {
  const queryClient = useQueryClient();
  const [months, setMonths] = useState(String(settings.duration_months));
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState<string | null>(null);

  const update = useMutation({
    mutationFn: adminApi.updatePromotionSettings,
    onSuccess: (data) => {
      setError(null);
      setMonths(String(data.settings.duration_months));
      setSaved(
        data.settings.enabled
          ? `New accounts are enrolled for ${data.settings.duration_months} months.`
          : 'New accounts are no longer enrolled. Existing windows are unchanged.',
      );
      void queryClient.invalidateQueries({ queryKey: ['admin', 'promotions'] });
    },
    onError: (err) =>
      setError(err instanceof ApiError ? err.message : 'Could not save the change.'),
  });

  const parsed = Number(months);
  const monthsValid =
    Number.isInteger(parsed) &&
    parsed >= settings.min_months &&
    parsed <= settings.max_months;

  return (
    <section className="rounded-card border border-hull-700 bg-hull-800/40 p-6">
      <h2 className="text-base font-semibold text-white">Programme</h2>
      <p className="mt-1 max-w-2xl text-sm text-hull-400">
        Whether newly activated accounts are enrolled, and for how long. This
        governs enrolment only — switching it off does not shorten a window
        somebody has already been granted. To end one account&rsquo;s window
        early, use Revoke below.
      </p>

      {error && <div className="mt-4"><Alert tone="error">{error}</Alert></div>}
      {saved && !error && <div className="mt-4"><Alert tone="success">{saved}</Alert></div>}

      <div className="mt-5 flex flex-wrap items-end gap-4">
        <div className="flex items-center gap-3">
          <span
            className={cx(
              'inline-block whitespace-nowrap rounded-full border px-2.5 py-0.5 text-xs',
              settings.enabled
                ? 'border-pass-600/40 bg-pass-600/10 text-pass-400'
                : 'border-hull-600 bg-hull-800 text-hull-300',
            )}
          >
            {settings.enabled ? 'Enrolling new accounts' : 'Not enrolling'}
          </span>
          <Button
            variant="secondary"
            disabled={update.isPending}
            onClick={() => update.mutate({ enabled: !settings.enabled })}
          >
            {settings.enabled ? 'Switch off' : 'Switch on'}
          </Button>
        </div>

        <div className="min-w-0">
          <label htmlFor="promo-months" className="mb-1.5 block text-xs text-hull-400">
            Window granted at signup (months)
          </label>
          <div className="flex items-end gap-2">
            <Input
              id="promo-months"
              type="number"
              min={settings.min_months}
              max={settings.max_months}
              value={months}
              onChange={(event) => setMonths(event.target.value)}
              className="w-24"
            />
            <Button
              variant="secondary"
              disabled={
                update.isPending || !monthsValid || parsed === settings.duration_months
              }
              onClick={() => update.mutate({ duration_months: parsed })}
            >
              Save
            </Button>
          </div>
          {!monthsValid && (
            <p className="mt-1.5 text-xs text-fail-400">
              Between {settings.min_months} and {settings.max_months} months.
            </p>
          )}
        </div>
      </div>

      <p className="mt-4 text-xs text-hull-400">
        A change to the window length applies to accounts enrolled after it.
        Nobody already enrolled is affected.
      </p>
    </section>
  );
}

/** The bulk grant: find accounts, select them, give them another window. */
function GrantPanel() {
  const queryClient = useQueryClient();

  const [filter, setFilter] = useState<PromotionState | ''>('awaiting_payment');
  const [search, setSearch] = useState('');
  const [submitted, setSubmitted] = useState('');
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [months, setMonths] = useState('3');
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [granted, setGranted] = useState<GrantOutcome[] | null>(null);
  const [revoked, setRevoked] = useState<number | null>(null);

  const users = useQuery({
    queryKey: ['admin', 'promotion-users', filter, submitted, page],
    queryFn: () =>
      adminApi.getUsers({
        search: submitted || undefined,
        promotion: filter || undefined,
        page,
        pageSize: PAGE_SIZE,
      }),
  });

  const rows = useMemo(() => users.data?.rows ?? [], [users.data]);
  const pages = users.data
    ? Math.max(1, Math.ceil(users.data.total / users.data.page_size))
    : 1;
  const allOnPageSelected = rows.length > 0 && rows.every((row) => selected.has(row.id));

  function clearOutcome() {
    setError(null);
    setGranted(null);
    setRevoked(null);
  }

  function toggle(id: string) {
    clearOutcome();
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function toggleAllOnPage() {
    clearOutcome();
    setSelected((current) => {
      const next = new Set(current);
      for (const row of rows) {
        if (allOnPageSelected) next.delete(row.id);
        else next.add(row.id);
      }
      return next;
    });
  }

  /** Both mutations settle the same way, so the handling lives in one place. */
  function afterWrite() {
    setSelected(new Set());
    void queryClient.invalidateQueries({ queryKey: ['admin', 'promotions'] });
    void queryClient.invalidateQueries({ queryKey: ['admin', 'promotion-users'] });
    // The directory reads the same rows under a different query key, and an
    // operator who grants here and then opens Users should not be shown the
    // state they have just changed.
    void queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
  }

  const grant = useMutation({
    mutationFn: () =>
      adminApi.grantPromotion({
        user_ids: [...selected],
        months: Number(months),
        reason: reason.trim() || null,
      }),
    onSuccess: (data) => {
      clearOutcome();
      setGranted(data.granted);
      afterWrite();
    },
    onError: (err) =>
      setError(err instanceof ApiError ? err.message : 'Could not grant the promotion.'),
  });

  const revoke = useMutation({
    mutationFn: () =>
      adminApi.revokePromotion({
        user_ids: [...selected],
        reason: reason.trim() || null,
      }),
    onSuccess: (data) => {
      clearOutcome();
      setRevoked(data.revoked.length);
      afterWrite();
    },
    onError: (err) =>
      setError(err instanceof ApiError ? err.message : 'Could not end the promotion.'),
  });

  const monthsValid = Number.isInteger(Number(months)) && Number(months) >= 1;
  const busy = grant.isPending || revoke.isPending;

  return (
    <section className="space-y-4">
      <div>
        <h2 className="text-base font-semibold text-white">Grant another period</h2>
        <p className="mt-1 max-w-2xl text-sm text-hull-400">
          Select accounts and give them another promotional window. An account
          whose window is still open is <strong className="text-hull-200">extended</strong>{' '}
          from the date it ends, so nobody loses time they already had; one whose
          window has closed starts afresh from today and comes straight back into
          service.
        </p>
      </div>

      <form
        className="flex flex-wrap items-end gap-3"
        onSubmit={(event) => {
          event.preventDefault();
          setSubmitted(search);
          setPage(1);
        }}
      >
        <div>
          <label htmlFor="promo-filter" className="mb-1.5 block text-xs text-hull-400">
            Show
          </label>
          <select
            id="promo-filter"
            value={filter}
            onChange={(event) => {
              setFilter(event.target.value as PromotionState | '');
              setPage(1);
            }}
            className="rounded-lg border border-hull-600 bg-hull-950 px-3 py-2.5 text-sm text-white"
          >
            {FILTERS.map((option) => (
              <option key={option.label} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        <div className="min-w-0 flex-1 sm:max-w-xs">
          <label htmlFor="promo-search" className="mb-1.5 block text-xs text-hull-400">
            Search
          </label>
          <Input
            id="promo-search"
            type="search"
            placeholder="Full email address or name"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
        </div>

        <Button type="submit" variant="secondary">
          Search
        </Button>
      </form>

      {error && <Alert tone="error">{error}</Alert>}

      {granted && <GrantedAlert granted={granted} />}

      {revoked !== null && (
        <Alert tone="warn">
          {revoked === 0
            ? 'None of the selected accounts had an open promotional window, so nothing changed.'
            : `${revoked} promotional window${revoked === 1 ? '' : 's'} ended. Any paid plan among them has stopped until it is paid for.`}
        </Alert>
      )}

      <AdminPanelState
        loading={users.isPending}
        error={users.error}
        empty={rows.length === 0}
        emptyTitle="No accounts match"
        emptyBody="Try a different promotion state, or clear the search term."
      >
        <div className="overflow-x-auto rounded-card border border-hull-700">
          <table className="w-full min-w-[44rem] text-left text-sm">
            <thead className="bg-hull-800/60 text-xs uppercase tracking-wider text-hull-400">
              <tr>
                <th scope="col" className="px-4 py-3 font-medium">
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={allOnPageSelected}
                      onChange={toggleAllOnPage}
                      className="h-4 w-4 rounded border-hull-600 bg-hull-950"
                    />
                    <span className="sr-only">Select every account on this page</span>
                  </label>
                </th>
                <th scope="col" className="px-4 py-3 font-medium">
                  User
                </th>
                <th scope="col" className="px-4 py-3 font-medium">
                  Plan
                </th>
                <th scope="col" className="px-4 py-3 font-medium">
                  Promotion
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id} className="border-t border-hull-800">
                  <td className="px-4 py-3">
                    <input
                      type="checkbox"
                      checked={selected.has(row.id)}
                      onChange={() => toggle(row.id)}
                      aria-label={`Select ${row.email}`}
                      className="h-4 w-4 rounded border-hull-600 bg-hull-950"
                    />
                  </td>
                  <td className="px-4 py-3">
                    <p className="font-medium text-white">{row.display_name ?? '—'}</p>
                    <p className="text-xs text-hull-400">{row.email}</p>
                  </td>
                  <td className="px-4 py-3 capitalize text-hull-200">
                    {row.plan ?? <span className="text-hull-500">none</span>}
                  </td>
                  <td className="px-4 py-3">
                    <PromotionCell row={row} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {pages > 1 && (
          <nav
            aria-label="Pagination"
            className="mt-4 flex items-center justify-between gap-4 text-sm"
          >
            <Button
              variant="secondary"
              disabled={page <= 1}
              onClick={() => setPage((current) => current - 1)}
            >
              Previous
            </Button>
            <span className="text-hull-400">
              Page {page} of {pages}
            </span>
            <Button
              variant="secondary"
              disabled={page >= pages}
              onClick={() => setPage((current) => current + 1)}
            >
              Next
            </Button>
          </nav>
        )}
      </AdminPanelState>

      {/* The selection survives paging and filtering, so the count is stated
          rather than left to be inferred from ticks on the current page. */}
      <div className="rounded-card border border-hull-600 bg-hull-800 p-5">
        <p className="text-sm text-hull-200">
          <strong className="text-white">{selected.size}</strong> account
          {selected.size === 1 ? '' : 's'} selected
          {selected.size > 0 && (
            <button
              type="button"
              onClick={() => {
                clearOutcome();
                setSelected(new Set());
              }}
              className="ml-3 text-xs text-surge-400 underline underline-offset-2 hover:text-surge-300"
            >
              Clear
            </button>
          )}
        </p>

        <div className="mt-4 flex flex-wrap items-end gap-3">
          <div>
            <label htmlFor="grant-months" className="mb-1.5 block text-xs text-hull-400">
              Months to grant
            </label>
            <Input
              id="grant-months"
              type="number"
              min={1}
              max={60}
              value={months}
              onChange={(event) => setMonths(event.target.value)}
              className="w-24"
            />
          </div>

          <div className="min-w-0 flex-1 sm:max-w-md">
            <label htmlFor="grant-reason" className="mb-1.5 block text-xs text-hull-400">
              Reason (recorded on the audit trail)
            </label>
            <Input
              id="grant-reason"
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              placeholder="Gateway not live yet"
              maxLength={256}
            />
          </div>

          <Button
            disabled={busy || selected.size === 0 || !monthsValid}
            onClick={() => grant.mutate()}
          >
            {grant.isPending ? 'Granting…' : `Grant ${months} month${months === '1' ? '' : 's'}`}
          </Button>

          <Button
            variant="secondary"
            disabled={busy || selected.size === 0}
            onClick={() => {
              // Confirmed, unlike the grant: this stops paid plans running,
              // immediately, for everyone selected.
              if (
                window.confirm(
                  `End the promotional window for ${selected.size} account${
                    selected.size === 1 ? '' : 's'
                  }? Any paid plan among them stops until it is paid for.`,
                )
              ) {
                revoke.mutate();
              }
            }}
          >
            {revoke.isPending ? 'Ending…' : 'Revoke'}
          </Button>
        </div>

        <p className="mt-3 text-xs text-hull-400">
          A grant works whether or not the programme is enrolling new accounts.
          Every grant and every revocation is written to the audit log with your
          address and the reason above.
        </p>
      </div>
    </section>
  );
}

/**
 * What the grant actually did, for one account or for many.
 *
 * A single account gets its own sentence with the date, because that is the
 * fact the operator is about to repeat to the customer. A bulk grant gets a
 * count, because twelve dates is not something anybody reads.
 */
function GrantedAlert({ granted }: { granted: GrantOutcome[] }) {
  const [only] = granted;

  if (granted.length === 1 && only) {
    return (
      <Alert tone="success">
        {only.email} — promotion {only.extended ? 'extended' : 'restarted'} to{' '}
        {formatDate(only.promotion_ends_at)}
        {only.reinstated && ', and their plan is running again'}.
      </Alert>
    );
  }

  const reinstated = granted.filter((row) => row.reinstated).length;
  return (
    <Alert tone="success">
      {granted.length} accounts granted another period.
      {reinstated > 0 &&
        ` ${reinstated} had a suspended plan put back into service.`}
    </Alert>
  );
}

function PromotionCell({ row }: { row: AdminUserRow }) {
  if (row.promotion_ends_at === null) {
    return <span className="text-xs text-hull-500">never enrolled</span>;
  }

  if (row.promotion_active) {
    return (
      <span className="text-xs text-pass-400">
        until {formatDate(row.promotion_ends_at)}
      </span>
    );
  }

  return (
    <span className={cx('text-xs', row.awaiting_payment ? 'text-warn-400' : 'text-hull-400')}>
      ended {formatDate(row.promotion_ends_at)}
      {row.awaiting_payment && ' · awaiting payment'}
    </span>
  );
}
